import { currencyFormatter } from "@common/helpers/currency";
import { ScheduleLifecycleProposal } from "@common/schemas/domains/accounting/scheduleLifecycle.schema";
import { ScheduleLifecycleVerb } from "@common/types/domains/accounting/schedule";
import {
    SCHEDULE_DELETE_BODY,
    SCHEDULE_DELETE_REMOVE_LABEL,
    SCHEDULE_DELETE_REVERSE_LABEL,
    buildScheduleDeleteTitle,
} from "../change/scheduleChangeCopy";
import { SCHEDULE_SOURCE_TYPE_LABELS } from "@common/helpers/domains/accounting/scheduleApplicationFilters";

export interface ScheduleLifecycleCopy {
    /** Names the schedule's source; the policy goes in the body. */
    title: string;
    policyLine: string;
    body: string;
    confirmLabel: string;
    /** Delete: the hard-delete answer, beside the reversal one. */
    alternativeLabel?: string;
    isDestructive: boolean;
}

export const MILESTONE_ACHIEVED_MENU_LABEL = "Milestone achieved";
export const SCHEDULE_PAUSE_BODY =
    "Journal entries dated on or before today will not be changed. Future journal entries will be deleted. Months between the pause and the resume date are skipped and not recognized.";
export const SCHEDULE_RESUME_SKIP_NOTE =
    "Months from this date on are recognized again, including past months. Months between the pause and this date are skipped and not recognized.";
export const SCHEDULE_MILESTONE_PAUSE_BODY =
    "Confirmed milestones won't change. Pending milestones are on hold until you resume.";
export const SCHEDULE_MILESTONE_RESUME_BODY =
    "Paused milestones go back to pending.";
export const SCHEDULE_STOP_BODY =
    "Future recognition will stop and past entries won't change. This action can't be reverted.";
export const VIEW_RECOGNITION_SCHEDULE_LABEL = "View recognition schedule";

export function formatSchedulePolicyLine(templateName: string): string {
    return `Policy: ${templateName}`;
}

export const SCHEDULE_LIFECYCLE_MENU_LABELS: Record<
    ScheduleLifecycleVerb,
    string
> = {
    [ScheduleLifecycleVerb.change]: "Change this schedule",
    [ScheduleLifecycleVerb.pause]: "Pause recognition",
    [ScheduleLifecycleVerb.resume]: "Resume recognition",
    [ScheduleLifecycleVerb.cancel]: "Stop schedule",
    [ScheduleLifecycleVerb.delete]: "Remove schedule",
};

export const SCHEDULE_LIFECYCLE_SUCCESS_TOASTS: Record<
    ScheduleLifecycleVerb,
    string
> = {
    [ScheduleLifecycleVerb.change]: "Schedule updated",
    [ScheduleLifecycleVerb.pause]: "Recognition paused",
    [ScheduleLifecycleVerb.resume]: "Recognition resumed",
    [ScheduleLifecycleVerb.cancel]: "Schedule stopped",
    [ScheduleLifecycleVerb.delete]: "Schedule removed",
};

export function buildScheduleLifecycleCopy(
    proposal: ScheduleLifecycleProposal,
): ScheduleLifecycleCopy {
    const { summary } = proposal;
    const policyLine = formatSchedulePolicyLine(summary.templateName);
    switch (proposal.verb) {
        case ScheduleLifecycleVerb.delete:
            return {
                title: buildScheduleDeleteTitle(summary.sourceLabel),
                policyLine,
                body: SCHEDULE_DELETE_BODY,
                confirmLabel: SCHEDULE_DELETE_REVERSE_LABEL,
                alternativeLabel: SCHEDULE_DELETE_REMOVE_LABEL,
                isDestructive: false,
            };
        case ScheduleLifecycleVerb.pause:
            return {
                title: `Pause ${summary.sourceLabel}?`,
                policyLine,
                body: summary.isMilestone
                    ? SCHEDULE_MILESTONE_PAUSE_BODY
                    : SCHEDULE_PAUSE_BODY,
                confirmLabel: "Pause schedule",
                isDestructive: false,
            };
        case ScheduleLifecycleVerb.resume:
            return {
                title: `Resume ${summary.sourceLabel}?`,
                policyLine,
                body: summary.isMilestone
                    ? SCHEDULE_MILESTONE_RESUME_BODY
                    : `Select a date to resume the recognition schedule. ${SCHEDULE_RESUME_SKIP_NOTE}`,
                confirmLabel: "Resume schedule",
                isDestructive: false,
            };
        case ScheduleLifecycleVerb.cancel:
            return {
                title: `Stop ${summary.sourceLabel}?`,
                policyLine,
                body: SCHEDULE_STOP_BODY,
                confirmLabel: "Stop schedule",
                isDestructive: false,
            };
        case ScheduleLifecycleVerb.change: {
            const amountSentence =
                summary.previousAmount !== summary.newAmount
                    ? `${SCHEDULE_SOURCE_TYPE_LABELS[summary.sourceType]} amount changed from ${currencyFormatter.format(summary.previousAmount ?? 0)} to ${currencyFormatter.format(summary.newAmount ?? 0)}. `
                    : "";
            return {
                title: `Change ${summary.sourceLabel}?`,
                policyLine,
                body: `${amountSentence}Future recognition will be re-spread with the updated terms. This action can't be reverted.`,
                confirmLabel: "Change schedule",
                isDestructive: false,
            };
        }
    }
}
